import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import {
    fetchUserSyncJob,
    isUserSyncFailedStatus,
    isUserSyncTerminalStatus,
    syncAppUsers,
    type AdminRegistryApp,
} from '@/api/console';
import { Button, Callout, Modal } from '@/components/ui';
import { describeSyncError } from './syncErrorMessages';
import styles from './syncResult.module.css';
import importStyles from './importUsers.module.css';

export interface SyncUsersModalProps {
    app: AdminRegistryApp | null;
    onClose: () => void;
    /** EAA-1861: escape hatch to `ImportUsersModal` — surfaced regardless of
     *  error state rather than sniffed off `ScimNotConfigured`'s message text,
     *  since matching backend prose is fragile and an operator may prefer the
     *  file path even when live pull IS configured (e.g. a one-off batch). */
    onSwitchToImport: () => void;
}

/**
 * EAA-1861 (design frame "EAA-1861-B Sync users — result dialog", added to
 * the Claude Design project alongside this feature since no existing frame
 * covered it): live pull of one app's own user directory
 * (`POST /admin/apps/{code}/sync-users`), matched by email/`external_sub` —
 * an existing Keycloak identity is only LINKED, a new one is minted with a
 * forced password reset (the portal never sees the app's own password).
 *
 * Deliberately never shows a temp password, even for freshly-created
 * accounts: at bulk scale there is no single "reveal once" moment that makes
 * sense for N rows, and this session's design decision was to leave
 * credential handoff to the existing `ResetCredentialFields` flow — an
 * admin who needs to hand a user their first password uses that, per
 * account, when they are actually ready to.
 */
export function SyncUsersModal({ app, onClose, onSwitchToImport }: SyncUsersModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();

    // EAA-1886: the POST went asynchronous. Minting Keycloak identities inline for
    // 100+ users blew gunicorn's worker timeout, so the backend now enqueues a job
    // and answers 202 with `{ job_id, status }` only — the counts this dialog
    // renders live on the job endpoint and have to be polled for. The modal
    // therefore holds the job id it was handed and drives a `useQuery` that
    // re-fetches until the job reaches a terminal status.
    const [jobId, setJobId] = useState<string | null>(null);

    const syncMutation = useMutation({
        mutationFn: () =>
            syncAppUsers(config.apiBase, accessToken as string, (app as AdminRegistryApp).code),
        onSuccess: (handle) => setJobId(handle.job_id),
    });

    const jobQuery = useQuery({
        queryKey: ['app-sync-users-job', app?.code, jobId],
        queryFn: () =>
            fetchUserSyncJob(
                config.apiBase,
                accessToken as string,
                (app as AdminRegistryApp).code,
                jobId as string
            ),
        enabled: app !== null && jobId !== null,
        retry: false,
        // EAA-1886: poll every 2s while the job is queued/running, then stop dead
        // on a terminal status so a finished dialog is not left hammering the API.
        refetchInterval: (query) =>
            isUserSyncTerminalStatus(query.state.data?.status) ? false : 2000,
    });

    const job = jobQuery.data ?? null;
    const jobStatus = job?.status;
    // EAA-1886: `pending` and `running` are BOTH non-terminal — a job waits at
    // `pending` until a worker picks it up, which can take a while, so treating
    // it as anything but "still going" would end the poll before it started.
    const isJobTerminal = isUserSyncTerminalStatus(jobStatus);
    const isJobFailed = isUserSyncFailedStatus(jobStatus);
    const isRunning =
        syncMutation.isPending || (jobId !== null && !isJobTerminal && !jobQuery.isError);
    const hasError = syncMutation.isError || jobQuery.isError || isJobFailed;
    // EAA-1886: a pending/running job has no counts yet, and a shape surprise must
    // degrade rather than white-screen (the previous crash was `data.errors.length`
    // on an undefined `errors`) — so the result block is gated on a terminal,
    // non-failed job and every list is still read through a `?? []` guard below.
    const result = isJobTerminal && !isJobFailed ? job : null;
    const errorRows = result?.errors ?? [];
    const skippedRows = result?.skipped ?? [];
    // EAA-1886: progress counts BATCHES, not records — the backend pre-rounds
    // `percentage` to 1dp, so render it as-is rather than recomputing it here and
    // risking a number that disagrees with what the job itself reports.
    const batchesTotal = job?.progress?.batches_total;
    const batchesCompleted = job?.progress?.batches_completed ?? 0;
    const percentage = job?.progress?.percentage ?? 0;
    const jobErrorDetail = job?.error_detail?.trim();

    // EAA-1886: a new/closed app must not keep polling the previous app's job.
    useEffect(() => {
        setJobId(null);
    }, [app?.code]);

    function handleClose() {
        if (isRunning) return;
        onClose();
    }

    function handleRun() {
        setJobId(null);
        syncMutation.mutate();
    }

    return (
        <Modal
            open={Boolean(app)}
            onCancel={handleClose}
            title={t('console.apps.syncUsersModal.title', { name: app?.name })}
            width={520}
            footer={
                <>
                    <Button onClick={handleClose} disabled={isRunning}>
                        {t('common.close')}
                    </Button>
                    <Button type="primary" onClick={handleRun} disabled={isRunning}>
                        {isRunning
                            ? t('console.apps.syncUsersModal.runningLabel')
                            : jobId !== null
                              ? t('console.apps.syncUsersModal.rerunButton')
                              : t('console.apps.syncUsersModal.runButton')}
                    </Button>
                </>
            }
        >
            <div className={styles.root}>
                <p>
                    <Trans
                        i18nKey="console.apps.syncUsersModal.description"
                        values={{ name: app?.name }}
                        components={{ bold: <strong /> }}
                    />
                </p>

                {/* EAA-1886: a whole-job failure before any batch ran carries the real
                 *  cause on `error_detail` (e.g. `ScimNotConfigured`). Show that verbatim
                 *  when present — the generic copy tells an operator nothing they can act
                 *  on, and this is the one field that does. */}
                {hasError && (
                    <p className="portal-admin-error">
                        {isJobFailed
                            ? (jobErrorDetail ?? '') !== ''
                                ? jobErrorDetail
                                : t('console.apps.syncUsersModal.jobFailedMessage')
                            : t('console.apps.syncUsersModal.errorMessage')}
                    </p>
                )}

                {isRunning && (
                    <p className="portal-admin-hint">
                        {batchesTotal !== undefined && batchesTotal > 0
                            ? t('console.apps.syncUsersModal.jobProgress', {
                                  percentage,
                                  completed: batchesCompleted,
                                  total: batchesTotal,
                              })
                            : t('console.apps.syncUsersModal.jobRunning')}
                    </p>
                )}

                {result && (
                    <>
                        <div className={styles.stats}>
                            <div className={`${styles.stat} ${styles.statCreated}`}>
                                <span className={styles.statValue}>{result.created ?? 0}</span>
                                <span className={styles.statLabel}>
                                    {t('console.apps.syncUsersModal.statCreated')}
                                </span>
                            </div>
                            <div className={`${styles.stat} ${styles.statLinked}`}>
                                <span className={styles.statValue}>{result.linked ?? 0}</span>
                                <span className={styles.statLabel}>
                                    {t('console.apps.syncUsersModal.statLinked')}
                                </span>
                            </div>
                            <div className={`${styles.stat} ${styles.statErrors}`}>
                                <span className={styles.statValue}>{errorRows.length}</span>
                                <span className={styles.statLabel}>
                                    {t('console.apps.syncUsersModal.statErrors')}
                                </span>
                            </div>
                            {/* EAA-1883: `skipped` is a terminal, non-actionable state (e.g. a
                             *  system admin with no school) — a distinct, neutral stat rather
                             *  than folded into the red `errors` count, so operators do not read
                             *  it as something to fix. */}
                            <div className={`${styles.stat} ${styles.statSkipped}`}>
                                <span className={styles.statValue}>{skippedRows.length}</span>
                                <span className={styles.statLabel}>
                                    {t('console.apps.syncUsersModal.statSkipped')}
                                </span>
                            </div>
                        </div>

                        {errorRows.length > 0 && (
                            <div className={styles.errorBox}>
                                <span className={styles.errorBoxTitle}>
                                    {t('console.apps.syncUsersModal.errorRowsTitle', {
                                        count: errorRows.length,
                                    })}
                                </span>
                                <ul className={styles.errorList}>
                                    {errorRows.map((row) => {
                                        // EAA-1883: known `error_code` values render a friendly,
                                        // translated sentence; an absent/unrecognized code falls back
                                        // to the raw backend string under a "technical error" badge
                                        // rather than hiding it — see `syncErrorMessages.ts`.
                                        const display = describeSyncError(row, t);
                                        return (
                                            <li key={row.external_id}>
                                                external_id={row.external_id} —{' '}
                                                {!display.isKnown && (
                                                    <span
                                                        className={`portal-console-badge is-warn ${styles.errorBadge}`}
                                                    >
                                                        {t('console.apps.syncErrors.unknownLabel')}
                                                    </span>
                                                )}
                                                {display.text}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}

                        {/* EAA-1883: rendered as its own neutral/info section, NEVER inside
                         *  the red error list above — `skipped` (currently only
                         *  `no_scope_id`) is a terminal state an operator cannot act on, so
                         *  it needs a visual identity that reads as "fine, not an action
                         *  item" rather than a problem to chase. */}
                        {skippedRows.length > 0 && (
                            <div className={styles.skippedBox}>
                                <span className={styles.skippedBoxTitle}>
                                    {t('console.apps.syncUsersModal.skippedRowsTitle', {
                                        count: skippedRows.length,
                                    })}
                                </span>
                                <p className={styles.skippedNote}>
                                    {t('console.apps.syncUsersModal.skippedNote')}
                                </p>
                                <ul className={styles.skippedList}>
                                    {skippedRows.map((row) => {
                                        const display = describeSyncError(row, t);
                                        return (
                                            <li key={row.external_id}>
                                                external_id={row.external_id} —{' '}
                                                {!display.isKnown && (
                                                    <span
                                                        className={`portal-console-badge is-warn ${styles.errorBadge}`}
                                                    >
                                                        {t('console.apps.syncErrors.unknownLabel')}
                                                    </span>
                                                )}
                                                {display.text}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </>
                )}

                <Callout tone="info">{t('console.apps.syncUsersModal.credentialNote')}</Callout>

                {/* EAA-1804: raw `<button class="apps-import-users__template-link">` ->
                    kit `Button type="link"` — same link-styled CSS as ImportUsersModal's
                    download-template action; see that file's comment for why `type="link"`
                    matches rather than changes the appearance. */}
                <Button
                    type="link"
                    className={importStyles.templateLink}
                    onClick={onSwitchToImport}
                >
                    {t('console.apps.syncUsersModal.switchToImportLink')}
                </Button>
            </div>
        </Modal>
    );
}

export default SyncUsersModal;
